/**
 * /tenants/$slug/authentication/conditional-access/app-access/$appId — route shim.
 *
 * The loader prefetches both the application detail AND the
 * per-app access set so the editor's first paint shows both
 * pickers populated without a loading spinner. The tenant
 * inventory queries (groups / users) are not prefetched — they're
 * the secondary content on this page and loading-state during the
 * first second is acceptable for them.
 */

import { createFileRoute, redirect } from "@tanstack/react-router";
import { fetchApplication } from "@/features/applications/queries";
import { fetchAccessSet } from "@/features/policies/app-access-queries";
import { queryKeys } from "@/lib/query-keys";
import { AppAccessEditorPage } from "@/features/policies/pages/app-access-editor-page";
import { canManageApplicationAssignments } from "@/features/applications/application-workspace";

export const Route = createFileRoute(
  "/_authed/tenants/$slug/authentication/conditional-access/app-access/$appId",
)({
  beforeLoad: ({ context, params }) => {
    if (!canManageApplicationAssignments(context.authority.permissions)) {
      throw redirect({
        to: "/tenants/$slug/applications/$appId",
        params: { slug: params.slug, appId: params.appId },
        search: { section: "assignments" },
        replace: true,
      });
    }
  },
  loader: ({ context, params }) =>
    Promise.all([
      context.queryClient.ensureQueryData({
        queryKey: queryKeys.applications.detail(context.tenant.id, params.appId),
        queryFn: () => fetchApplication(context.tenant.id, params.appId),
      }),
      context.queryClient.ensureQueryData({
        queryKey: queryKeys.applications.access(context.tenant.id, params.appId),
        queryFn: () => fetchAccessSet(context.tenant.id, params.appId),
      }),
    ]),
  component: AppAccessEditorPage,
});
